import { AbstractControl } from '@angular/forms';
import {
  marriedMaritalStatus,
  dependentWorkerType,
  independentWorkerType,
  otherWorkerType,
  EMAIL_REGEX,
  partnerMaritalStatus,
  MIN_INITIAL_INVESTMENT,
  MIN_MONTHLY_INVESTMENT,
} from '@constants/miscellaneous';
import { isNumeric } from 'rxjs/util/isNumeric';
import Utils from './util';

const validationError = { isError: true };

const ALLOWED_SPOUSE_PASSPORTS = [
  '157231904',
];

export function validateRut(c: AbstractControl) {
    if (!c.value) {
        return validationError;
    }
    let rut = Utils.formatToRut(c.value);
    rut = rut.replace(/\./g, '');
    rut = rut.replace(/-/g, '');
    const body = rut.slice(0, -1);
    let checkDigit = rut.slice(-1).toUpperCase();

    if (body.length < 7) {
        return validationError;
    }
    let addition = 0;
    let multiple = 2;
    let index = 0;
    for (let i = 1; i <= body.length; i++) {

        index = multiple * parseInt(rut.charAt(body.length - i), 10);
        addition = addition + index;

        if (multiple < 7) {
            multiple = multiple + 1;
        } else {
            multiple = 2;
        }

    }
    const expectedCheckDigit = 11 - (addition % 11);
    checkDigit = (checkDigit === 'K') ? '10' : checkDigit;
    checkDigit = (checkDigit === '0') ? '11' : checkDigit;

    if (expectedCheckDigit !== parseInt(checkDigit, 10)) {
        return validationError;
    }
    return null;
}

export function validateLastnames(c: AbstractControl) {
    if (!c.value) {
        return validationError;
    }
    const parent = c.parent;
    if (!parent) {
        return validationError;
    }
    const nationality = parent.controls['nationality'];
    if (nationality && nationality.value === 'CL') {
        const lastnames = (c.value as string).trim().split(' ');
        if (lastnames.length === 1) {
            return validationError;
        }
    }
    return null;
}

export function validateLastnamesForNationality(c: AbstractControl) {
    const parent = c.parent;
    if (!parent) {
        return validationError;
    } else if (parent.controls['lastNames']) {
        parent.controls['lastNames'].updateValueAndValidity();
    }
    return null;
}

export function validatePassword(c: AbstractControl) {

    if (!c.value) {
        return validationError;
    }
    // New regex: Alphanumeric + at least one uppercase + special character + min 8 chars
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

    return passwordRegex.test(c.value) ? null : {
        validatePassword: {
          valid: false,
        },
      };

}

export function validateLoginPassword(c: AbstractControl) {

    if (!c.value) {
        return validationError;
    }
    const passwordRegex = /^(?=.{8,}$)\D*\d/;

    return passwordRegex.test(c.value) ? null : {
        validateLoginPassword: {
          valid: false,
        },
      };

}

export function validateAddress(c: AbstractControl) {
    return !c.value || (/\d/.test(c.value) && /[a-zA-Z]/.test(c.value)) ? null : validationError;
}

export function validateMonthlyAndInitialInvestment(type: 'monthly'| 'initial') {
  return(c: AbstractControl) => {
    const parent = c.parent;
    if (!parent) {
      return validationError;
    }
    const minInitialInvestment = MIN_INITIAL_INVESTMENT;
    const minMonthlyInvestment = MIN_MONTHLY_INVESTMENT;
    const initialInvestment = parent.controls['initialInvestmentAmount'];
    const monthlyInvestment = parent.controls['monthlyInvestmentAmount'];
    const initialValue = initialInvestment.value;
    const monthlyValue = monthlyInvestment.value;
    if (c.value > 0) {
      if (
        type === 'monthly' &&
        (c.value < minMonthlyInvestment && (initialValue === 0 || initialValue === null || initialValue < minInitialInvestment))
      ) {
        return validationError;
      } else if (
        type === 'initial' &&
        (c.value < minInitialInvestment && (monthlyValue === 0 || monthlyValue === null || monthlyValue < minMonthlyInvestment))
      ) {
        return validationError;
      }
    }
    if (initialInvestment && monthlyInvestment) {
      if (
        (initialValue === 0 && monthlyValue === 0) ||
        (initialValue === null && monthlyValue === null) ||
        (monthlyValue === 0 && initialValue === null) ||
        (initialValue === 0 && monthlyValue === null)
      ) {
        return validationError;
      }
    }
    return null;
  };
}

export function validateEmail(email: string) { return EMAIL_REGEX.test(email); }

export function validateMaritalStatus(type: string) {
    return(c: AbstractControl) => {
        const parent = c.parent;
        if (!parent) {
            return validationError;
        }
        const maritalStatus = parent.controls['maritalStatus'];
        if ((maritalStatus.value === marriedMaritalStatus) || (maritalStatus.value === partnerMaritalStatus)) {
            if (type === 'omit') {
                return null;
            } else if (!c.value || c.value.length === 0) {
                return validationError;
            } else if (type === 'rut') {
                const rawValue = c.value.replace(/\./g, '').replace(/-/g, '');
                if (ALLOWED_SPOUSE_PASSPORTS.includes(rawValue)) {
                    return null;
                }
                const validRut = validateRut(c);
                if (validRut !== null) {
                    return validationError;
                }
            } else if (type === 'email') {
                const validEmail = validateEmail(c.value);
                if (!validEmail) {
                    return validationError;
                }
            } else if (type === 'phone') {
                if (c.value.length !== 15) {
                    return validationError;
                }
            }
        }
        return null;
    };
}

export function validateDependentWorker(type: string) {
    return(c: AbstractControl) => {
        const parent = c.parent;
        if (!parent) {
            return validationError;
        }
        const workerType = parent.controls['workerType'];
        if (workerType.value === dependentWorkerType) {
            if (!c.value && type === 'rut') {
                return null;
            } else if (!c.value && type !== 'rut') {
                return validationError;
            } else if (type === 'rut') {
                const validRut = validateRut(c);
                if (validRut !== null) {
                    return validationError;
                }
            }
        }
        return null;
    };
}

export function validateDirectorOf(type: string) {
    return(c: AbstractControl) => {
        const parent = c.parent;
        if (!parent) {
            return validationError;
        }
        if (type === 'directorOf') {
            return null;
        }
        const directorOf = parent.controls['directorOf'];
        if (directorOf.value && !c.value) {
            return validationError;
        }
        return null;
    };
}

export function validateIsPep(type: string) {
    return(c: AbstractControl) => {
        const parent = c.parent;
        if (!parent) {
            return validationError;
        }
        if (type === 'isPEP') {
            return null;
        }
        const isPEP = parent.controls['isPEP'];
        if (isPEP.value && !c.value) {
            return validationError;
        }
        return null;
    };
}

export function validateOtherWorker(c: AbstractControl) {
    const parent = c.parent;
    if (!parent) {
        return validationError;
    }
    const workerType = parent.controls['workerType'];
    if (workerType.value === otherWorkerType && !c.value) {
        return validationError;
    }
    return null;
}

export function validateDependentOrIndependentWorker(c: AbstractControl) {
    const parent = c.parent;
    if (!parent) {
        return validationError;
    }
    const workerType = parent.controls['workerType'];
    if ((workerType.value === independentWorkerType || workerType.value === dependentWorkerType) && !c.value) {
        return validationError;
    }
    return null;
}

export function validateIsUSPerson(type: string) {
    return(c: AbstractControl) => {
        const parent = c.parent;

        if (!parent) {
            return validationError;
        }
        if (type === 'isUSPerson') {
            return null;
        }
        const isUSPerson = parent.controls['isUSPerson'];
        if (isUSPerson.value && !c.value) {
            return validationError;
        }
        const ssnRegex = /^[0-9]{9}$/;
        if (isUSPerson.value && !ssnRegex.test(c.value)) {
            return validationError;
        }
        return null;
    };
}
